import { Popover } from "@headlessui/react";
import {
  IconBuildingStore,
  IconChevronDown,
  IconLogout,
  IconSettings,
  IconSwitchHorizontal,
  IconUser,
} from "@tabler/icons-react";
import classNames from "classnames";
import { useChangeUser, useFetchUserAccesses } from "@common/features/Team";
import { useIsTeammate, useMasterUser, useUser } from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import { Button } from "@app/elements/form";
import { FadeTransition } from "@app/elements/helpers";
import { Loading } from "@app/elements/loading";
import { useLogout } from "@app/features/user/queries/user";
import { useDocumentDirection } from "@app/hooks";

export default function HeaderUser() {
  const user = useUser();
  const masterUser = useMasterUser();
  const direction = useDocumentDirection();

  const isTeammate = useIsTeammate();

  const { data: userAccesses } = useFetchUserAccesses();
  const changeUser = useChangeUser();

  const activeUser = isTeammate
    ? (userAccesses || []).find((user) => user.master_user_id === masterUser.id)
    : {
        name: user.name,
        email: user.email,
      };
  const currentWorkspace = activeUser || {
    name: user.name,
    email: user.email,
  };
  const otherWorkspaces = [
    ...(isTeammate
      ? [
          {
            master_user_id: undefined,
            name: user.name,
            email: user.email,
            own: true,
          },
        ]
      : []),
    ...(userAccesses || [])
      .filter((team) => team.master_user_id !== masterUser.id)
      .map((team) => ({
        ...team,
        own: false,
      })),
  ];
  const logout = useLogout();
  const currentWorkspaceTitle = __("%{name} dashboard", {
    name: currentWorkspace.name || "",
  }).trim();

  return (
    <div>
      {changeUser.isPending && (
        <div className="absolute inset-0 z-50">
          <Loading rounded={false} />
        </div>
      )}
      <Popover as="div" className="relative inline-block">
        {({ open }) => (
          <>
            <Popover.Button
              title={__("Switch workspace")}
              className={classNames(
                "flex h-11 items-center gap-2.5 rounded-lg border border-transparent px-2.5 text-start text-foreground transition-all duration-150 hover:border-border hover:bg-muted focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                open && "border-primary/20 bg-primary/5",
                !open && "focus:border-transparent focus:bg-transparent",
              )}
              onMouseUp={(event) => event.currentTarget.blur()}
              onTouchEnd={(event) => event.currentTarget.blur()}
            >
              <span className="sr-only">{__("Open user menu")}</span>
              <div className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <IconUser size={18} stroke={2} />
                {otherWorkspaces.length !== 0 && (
                  <span className="absolute -bottom-1 -end-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white ring-2 ring-card">
                    <IconSwitchHorizontal size={11} stroke={2} />
                  </span>
                )}
              </div>
              <div
                className="hidden min-w-0 max-w-44 flex-col text-start md:flex"
                dir={direction}
              >
                <span className="block max-w-full truncate text-start text-sm font-bold leading-5 text-foreground">
                  {currentWorkspaceTitle}
                </span>
                <span className="block max-w-full truncate text-start text-xs font-semibold leading-4 text-muted-foreground">
                  {otherWorkspaces.length !== 0
                    ? __("Switch workspace")
                    : __("Profile settings")}
                </span>
              </div>
              <IconChevronDown
                className={classNames(
                  "text-muted-foreground transition-transform",
                  open && "rotate-180",
                )}
                size={17}
                stroke={2}
              />
            </Popover.Button>

            <FadeTransition>
              <Popover.Panel
                anchor={{ to: "bottom start", gap: 10, padding: 16 }}
                portal
                className="z-popover w-[min(23rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-border bg-popover text-start text-popover-foreground shadow-medium focus:outline-none"
                dir={direction}
              >
                <div className="border-b border-border bg-card px-4 py-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-muted-foreground">
                      <IconUser size={21} stroke={2} />
                    </span>
                    <div className="min-w-0 flex-1 text-start">
                      <p className="text-xs font-medium leading-5 text-muted-foreground">
                        {__("Current workspace")}
                      </p>
                      <h3 className="truncate text-sm font-bold leading-6 text-foreground">
                        {currentWorkspace.name}
                      </h3>
                      <p className="truncate text-xs font-medium leading-5 text-muted-foreground">
                        {currentWorkspace.email}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 border-b border-border bg-background px-4 py-3">
                  <Popover.Button
                    as={Button as any}
                    variant="outline-light"
                    to="/user/profile"
                    fullWidth
                    className="h-10 justify-center rounded-md px-3 text-sm font-semibold"
                  >
                    <IconSettings size={18} stroke={2} />
                    <span>{__("Profile settings")}</span>
                  </Popover.Button>
                  <Button
                    variant="outline-light"
                    fullWidth
                    className="h-10 justify-center rounded-md px-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      logout.mutate(undefined, {
                        onError: () => (window.location.href = "/login"),
                      });
                    }}
                  >
                    <IconLogout size={18} stroke={2} />
                    <span>{__("Logout")}</span>
                  </Button>
                </div>

                <div className="bg-card p-4">
                  <div className="mb-2 flex items-center justify-between gap-3 px-1">
                    <p className="text-xs font-bold leading-5 text-foreground">
                      {__("Switch workspace")}
                    </p>
                    <p className="text-xs font-medium leading-5 text-muted-foreground">
                      {otherWorkspaces.length}
                      {__("item")}
                    </p>
                  </div>

                  <div className="max-h-72 space-y-1 overflow-y-auto">
                    {otherWorkspaces.length === 0 ? (
                      <div className="rounded-md bg-muted px-4 py-5 text-center">
                        <p className="text-sm font-semibold text-foreground">
                          {__("No other workspace is available")}
                        </p>
                        <p className="mt-1 text-xs font-medium text-muted-foreground">
                          {__("Team access permissions appear here.")}
                        </p>
                      </div>
                    ) : (
                      otherWorkspaces.map((team) => (
                        <button
                          key={team.master_user_id || "own"}
                          type="button"
                          className="group flex w-full min-w-0 items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-start transition-colors hover:border-border hover:bg-muted/50"
                          onClick={() => changeUser.mutate(team.master_user_id)}
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            {team.own ? (
                              <IconUser size={20} stroke={2} />
                            ) : (
                              <IconBuildingStore size={20} stroke={2} />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-bold leading-5 text-foreground">
                              {team.own ? __("Personal workspace") : team.name}
                            </span>
                            <span className="mt-0.5 block truncate text-xs font-medium leading-5 text-muted-foreground">
                              {team.email}
                            </span>
                          </span>
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors group-hover:border-primary/30 group-hover:text-primary">
                            <IconSwitchHorizontal size={16} stroke={2} />
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </FadeTransition>
          </>
        )}
      </Popover>
    </div>
  );
}
